import { create } from 'zustand';
import { authAPI, usersAPI } from './api';

export const useAuthStore = create((set, get) => ({
  user: JSON.parse(localStorage.getItem('jepprep_user') || 'null'),
  isAuthenticated: !!localStorage.getItem('jepprep_user'),
  isLoading: false,
  isInitializing: true,
  error: null,

  initAuth: async () => {
    try {
      const response = await authAPI.getSession();
      if (response.data.authenticated) {
        const user = response.data.user;
        localStorage.setItem('jepprep_user', JSON.stringify(user));
        set({ user, isAuthenticated: true, isInitializing: false });
      } else {
        // Don't override if login() already authenticated the user while
        // this getSession() call was in flight (race condition fix)
        if (!get().isAuthenticated) {
          localStorage.removeItem('jepprep_user');
          set({ user: null, isAuthenticated: false, isInitializing: false });
        } else {
          set({ isInitializing: false });
        }
      }
    } catch {
      // Same guard: don't wipe a successful login
      if (!get().isAuthenticated) {
        localStorage.removeItem('jepprep_user');
        set({ user: null, isAuthenticated: false, isInitializing: false });
      } else {
        set({ isInitializing: false });
      }
    }
  },

  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authAPI.login({ email, password });
      const { user } = response.data;
      localStorage.setItem('jepprep_user', JSON.stringify(user));
      set({ user, isAuthenticated: true, isLoading: false });
      return { success: true };
    } catch (error) {
      let message;
      if (!error.response) {
        message = 'Unable to connect. Please check your internet connection and try again.';
      } else {
        message = error.response?.data?.detail || 'Login failed';
      }
      set({ error: message, isLoading: false });
      return { success: false, error: message };
    }
  },

  register: async (email, password, displayName) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authAPI.register({ email, password, display_name: displayName });
      const { user } = response.data;
      localStorage.setItem('jepprep_user', JSON.stringify(user));
      set({ user, isAuthenticated: true, isLoading: false });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      set({ error: message, isLoading: false });
      return { success: false, error: message };
    }
  },

  googleAuth: async (sessionId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authAPI.googleCallback(sessionId);
      const { user, needs_username } = response.data;
      localStorage.setItem('jepprep_user', JSON.stringify(user));
      set({ user, isAuthenticated: true, isLoading: false });
      return { success: true, needs_username };
    } catch (error) {
      const message = error.response?.data?.detail || 'Google sign-in failed';
      set({ error: message, isLoading: false });
      return { success: false, error: message };
    }
  },

  completeProfile: async (displayName) => {
    try {
      const response = await authAPI.completeProfile(displayName);
      const { user } = response.data;
      localStorage.setItem('jepprep_user', JSON.stringify(user));
      set({ user });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update profile';
      return { success: false, error: message };
    }
  },

  logout: async () => {
    try {
      await authAPI.logout();
    } catch {
      // Clear local state even if server call fails
    }
    localStorage.removeItem('jepprep_user');
    set({ user: null, isAuthenticated: false });
  },

  refreshUser: async () => {
    try {
      const response = await usersAPI.getMe();
      const user = response.data;
      localStorage.setItem('jepprep_user', JSON.stringify(user));
      set({ user });
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  },
}));
